/**
 * SpoutingConsumablesAdmin — Iter150-E.
 *
 * Office-level defaults editor for Spouting consumable rates. Every
 * new appraisal seeds its gutter-builder config with these values, so
 * the office can keep them in lock-step with the current supplier
 * rate sheet without per-job editing. Per-appraisal override is still
 * available via the inline "⚙ rates" pill in the Components tab's
 * Bracket Summary card.
 *
 * Backed by `system_settings` (key=`spouting_consumables_defaults`)
 * via the GET / PUT `/api/admin/spouting-consumables-defaults`
 * endpoints. PUT is admin-only.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Save, RotateCcw, Loader2, ShieldAlert } from 'lucide-react';

const HARDCODED_DEFAULTS = {
  dropper_rivets: 4,        dropper_silicone_pct: 10,
  mitre_pair_rivets: 5,     mitre_pair_silicone_pct: 15,
  stop_end_rivets: 4,       stop_end_silicone_pct: 15,
  // Iter155 — buffer percentages applied to Push to BOM totals.
  rivet_buffer_pct: 10,     silicone_buffer_pct: 10,
};

const CARDS = [
  {
    key: 'dropper', label: 'Per Dropper',
    desc: 'Each outlet (80mm / 65mm / Rect) installs one dropper. The chosen rivet + silicone qty applies per dropper installed.',
    rivetsField: 'dropper_rivets', pctField: 'dropper_silicone_pct',
    tone: 'amber',
  },
  {
    key: 'mitre_pair', label: 'Per Pair of Mitres',
    desc: 'Operator specifies the qty per pair (2 mitres) — the per-mitre figures are auto-halved for the appraisal calc.',
    rivetsField: 'mitre_pair_rivets', pctField: 'mitre_pair_silicone_pct',
    tone: 'cyan',
  },
  {
    key: 'stop_end', label: 'Per Stop End',
    desc: 'Applied to each left-hand and each right-hand stop end independently.',
    rivetsField: 'stop_end_rivets', pctField: 'stop_end_silicone_pct',
    tone: 'indigo',
  },
];

const TONE_CLS = {
  amber:  { ring: 'ring-amber-200',  head: 'text-amber-900',  bg: 'bg-amber-50/50',  inputRing: 'focus:ring-amber-300' },
  cyan:   { ring: 'ring-cyan-200',   head: 'text-cyan-900',   bg: 'bg-cyan-50/50',   inputRing: 'focus:ring-cyan-300' },
  indigo: { ring: 'ring-indigo-200', head: 'text-indigo-900', bg: 'bg-indigo-50/50', inputRing: 'focus:ring-indigo-300' },
};

export default function SpoutingConsumablesAdmin() {
  const { api, user } = useAuth();
  const isAdmin = (user?.role || '') === 'admin';
  const [values, setValues] = useState(HARDCODED_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/spouting-consumables-defaults');
      setValues({ ...HARDCODED_DEFAULTS, ...(data?.defaults || {}) });
      setDirty(false);
    } catch (e) {
      toast.error('Failed to load Spouting consumable defaults');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const update = (field, raw) => {
    let v;
    if (field === 'rivet_buffer_pct' || field === 'silicone_buffer_pct') {
      // Iter155 — buffer percentages clamp to 0-50 per spec (anything
      // bigger would double-order materials).
      v = Math.max(0, Math.min(50, parseFloat(raw) || 0));
    } else if (field.endsWith('_pct')) {
      v = Math.max(0, Math.min(100, parseFloat(raw) || 0));
    } else {
      v = Math.max(0, parseInt(raw, 10) || 0);
    }
    setValues(prev => ({ ...prev, [field]: v }));
    setDirty(true);
  };

  const save = async () => {
    if (!isAdmin) {
      toast.error('Admin role required to save office defaults.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/admin/spouting-consumables-defaults', values);
      setValues(data?.defaults || values);
      setDirty(false);
      toast.success('Office Spouting consumable defaults saved. New appraisals will seed from these values.');
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const resetToOperatorRules = () => {
    setValues(HARDCODED_DEFAULTS);
    setDirty(true);
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <main className="max-w-5xl mx-auto px-4 py-6">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Spouting Consumables · Office Defaults</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-3xl">
            Set the office's current per-component rivet + silicone allowances. Every
            new appraisal seeds its gutter-builder config from these values, and the
            Bracket Summary on the Components tab calculates total rivets / silicone tubes
            from them.
          </p>
          <p className="mt-2 text-xs text-slate-500 max-w-3xl">
            Operators can still override per-appraisal via the inline <em>⚙ rates</em> pill
            inside the Bracket Summary card on a specific appraisal — that override never
            touches the office default.
          </p>
        </header>

        {!isAdmin && (
          <div data-testid="spouting-defaults-readonly-banner" className="mb-4 flex items-start gap-2 p-3 rounded border border-amber-300 bg-amber-50 text-amber-900 text-sm">
            <ShieldAlert size={18} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Read-only view</div>
              <div className="text-xs">You can review the office defaults, but only an admin can update them. Ask an admin to make the change, or use the per-appraisal <em>⚙ rates</em> editor for a one-off job.</div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="animate-spin" size={16} /> Loading current defaults…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {CARDS.map(card => {
                const tone = TONE_CLS[card.tone];
                const rivets = values[card.rivetsField];
                const pct    = values[card.pctField];
                const isPair = card.key === 'mitre_pair';
                return (
                  <div
                    key={card.key}
                    data-testid={`spouting-defaults-card-${card.key}`}
                    className={`rounded-lg ring-1 ${tone.ring} ${tone.bg} bg-white p-4 flex flex-col gap-3`}
                  >
                    <div>
                      <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${tone.head}`}>{card.label}</div>
                      <p className="mt-1 text-[11px] text-slate-500 leading-snug">{card.desc}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex flex-col gap-1 text-xs">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rivets</span>
                        <input
                          type="number" step="1" min="0" max="50"
                          disabled={!isAdmin}
                          value={rivets}
                          onChange={(e) => update(card.rivetsField, e.target.value)}
                          data-testid={`spouting-defaults-${card.key}-rivets`}
                          className={`w-full px-2 py-1.5 border border-slate-300 rounded text-right font-mono-num text-sm focus:outline-none focus:ring-2 ${tone.inputRing} disabled:bg-slate-50 disabled:text-slate-500`}
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-xs">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Silicone %</span>
                        <input
                          type="number" step="0.5" min="0" max="100"
                          disabled={!isAdmin}
                          value={pct}
                          onChange={(e) => update(card.pctField, e.target.value)}
                          data-testid={`spouting-defaults-${card.key}-pct`}
                          className={`w-full px-2 py-1.5 border border-slate-300 rounded text-right font-mono-num text-sm focus:outline-none focus:ring-2 ${tone.inputRing} disabled:bg-slate-50 disabled:text-slate-500`}
                        />
                      </label>
                    </div>
                    {isPair && (
                      <div className="text-[10px] text-slate-400 italic">
                        ↳ Per-mitre derived: <strong className="font-mono-num not-italic">{rivets / 2}</strong> rivets · <strong className="font-mono-num not-italic">{pct / 2}</strong>% silicone
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Iter155 — Push-to-BOM buffer percentages */}
            <div className="mt-6 rounded-lg ring-1 ring-rose-200 bg-rose-50/40 p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-900">Push-to-BOM buffers</div>
              <p className="mt-1 mb-3 text-[11px] text-slate-500 leading-snug max-w-2xl">
                When the Bracket Summary pushes auto-rows to BOM, rivets and silicone tubes get a final buffer:
                rivets multiply by <strong>(1 + buffer/100)</strong>; silicone gets a <strong>next-tube nudge</strong> —
                if the fractional remainder of the rounded-up tube count is within the buffer threshold of a full tube,
                bump by one extra tube. Per-job override via the Bracket Summary <em>⚙ rates</em> pill.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl">
                <label className="flex flex-col gap-1 text-xs bg-white rounded ring-1 ring-rose-200 p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Rivet buffer %</span>
                  <input
                    type="number" step="1" min="0" max="50"
                    disabled={!isAdmin}
                    value={values.rivet_buffer_pct}
                    onChange={(e) => update('rivet_buffer_pct', e.target.value)}
                    data-testid="spouting-defaults-rivet-buffer-pct"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-right font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:bg-slate-50 disabled:text-slate-500"
                  />
                  <span className="text-[10px] text-slate-500 italic">final = ⌈raw × (1 + {values.rivet_buffer_pct}/100)⌉</span>
                </label>
                <label className="flex flex-col gap-1 text-xs bg-white rounded ring-1 ring-rose-200 p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Silicone buffer %</span>
                  <input
                    type="number" step="1" min="0" max="50"
                    disabled={!isAdmin}
                    value={values.silicone_buffer_pct}
                    onChange={(e) => update('silicone_buffer_pct', e.target.value)}
                    data-testid="spouting-defaults-silicone-buffer-pct"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-right font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:bg-slate-50 disabled:text-slate-500"
                  />
                  <span className="text-[10px] text-slate-500 italic">nudge when frac ≥ {(1 - values.silicone_buffer_pct / 100).toFixed(2)}</span>
                </label>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2">
              <button
                onClick={save}
                disabled={!isAdmin || !dirty || saving}
                data-testid="spouting-defaults-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#D35400] text-white text-sm font-semibold hover:bg-[#A04000] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                Save office defaults
              </button>
              <button
                onClick={resetToOperatorRules}
                disabled={!isAdmin || saving}
                data-testid="spouting-defaults-reset"
                title="Reset all fields to the operator's stated baseline (4r+10% / dropper, 5r+15% / pair of mitres, 4r+15% / stop end). Doesn't save until you click Save."
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-slate-700 ring-1 ring-slate-300 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <RotateCcw size={14} /> Reset to operator rules
              </button>
              {dirty && (
                <span data-testid="spouting-defaults-dirty" className="text-[11px] text-amber-700 font-semibold">Unsaved changes</span>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
